"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Native = void 0;
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const logger_1 = require("../utils/logger");
/* global WebKitMediaKeys */
const AbstractBaseEngine_1 = require("./AbstractBaseEngine");
const interfaces_1 = require("./interfaces");
const hls_1 = require("./utils/hls");
const NativeErrors_1 = require("./utils/NativeErrors");
const BITRATE_POLL_INTERVAL = 10 * 1000;
function createTextTrack(track) {
    return {
        id: track.language,
        language: track.language || (0, js_player_shared_1.getLabel)(track.language),
        kind: track.kind,
        label: track.label,
    };
}
class Native extends AbstractBaseEngine_1.AbstractBaseEngine {
    constructor(videoElement, instanceSettings) {
        var _a, _b;
        super(videoElement, instanceSettings);
        this.name = "Native HTML Video";
        this.playertechVersion = "N/A";
        this.instanceSettings = instanceSettings;
        this.authToken =
            (_b = (_a = instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.playToken) !== null && _b !== void 0 ? _b : instanceSettings.initOptions.playToken;
        this.onNeedKey = this.onNeedKey.bind(this);
        this.onKeyMessage = this.onKeyMessage.bind(this);
        this.onKeyAdded = this.onKeyAdded.bind(this);
        this.onKeyError = this.onKeyError.bind(this);
        this.videoElement.setAttribute("playsinline", "");
        this.setupEventListeners();
    }
    _load({ src, license, startTime, audio, subtitle, }) {
        const _super_1 = Object.create(null, {
            load: { get: () => super.load }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const _super = {
                load: _super_1.load.bind(this),
            };
            this.src = src;
            this.startTime = startTime;
            let drmEvaluationAndInitiationPromise;
            if (license === null || license === void 0 ? void 0 : license["com.apple.fps"]) {
                this.drmInfo = license["com.apple.fps"];
                const dataUrl = new URL(this.drmInfo.licenseServerUrl);
                this.contentId = (_a = dataUrl.searchParams.get("contentId")) !== null && _a !== void 0 ? _a : undefined;
                drmEvaluationAndInitiationPromise = this.initDrmSetup();
            }
            else if ((license === null || license === void 0 ? void 0 : license["com.microsoft.playready"]) ||
                (license === null || license === void 0 ? void 0 : license["com.widevine.alpha"])) {
                const licenseType = license["com.microsoft.playready"]
                    ? "playready"
                    : "widevine";
                this.emit(interfaces_1.EngineEvents.ERROR, new js_player_shared_1.PlayerError(`[Native] drm ${licenseType} is not supported, fairplay not provided`, {
                    type: js_player_shared_1.ErrorTypes.DRM,
                    rawError: license,
                }));
                return;
            }
            else {
                drmEvaluationAndInitiationPromise = Promise.resolve();
            }
            return drmEvaluationAndInitiationPromise.then(() => {
                this.videoElement.src = src;
                this.videoElement.load();
                this.videoElement.textTracks.addEventListener("addtrack", ({ track }) => {
                    if (track && track.kind === "metadata") {
                        track.mode = "hidden";
                        track.addEventListener("cuechange", (evt) => __awaiter(this, void 0, void 0, function* () {
                            const metadataEvent = {
                                event: evt,
                                engineName: "Native HTML Video",
                            };
                            this.emit(interfaces_1.EngineEvents.METADATA_EVENT, metadataEvent);
                        }));
                    }
                });
                _super.load({
                    src,
                    license,
                    startTime,
                    audio,
                    subtitle,
                });
            }, (error) => {
                if (error instanceof js_player_shared_1.PlayerError) {
                    this.emit(interfaces_1.EngineEvents.ERROR, error);
                }
                else {
                    this.emit(interfaces_1.EngineEvents.ERROR, new js_player_shared_1.PlayerError("[Native] could not initiate drm", {
                        type: js_player_shared_1.ErrorTypes.DRM,
                        rawError: error,
                    }));
                }
            });
        });
    }
    load(loadParameters) {
        // load doesn't return a promise but since we want to use async/await we call a private method
        // noinspection JSIgnoredPromiseFromCall
        this._load(loadParameters);
    }
    onLoaded() {
        var _a, _b;
        const startDate = (_b = (_a = this.videoElement).getStartDate) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (startDate && !isNaN(startDate.getTime())) {
            this.setUTCStartTime(startDate.getTime());
        }
        if (this.startTime !== undefined) {
            this.videoElement.currentTime = this.startTime;
        }
        if (this.src && this.src.includes(".m3u8")) {
            (0, hls_1.getPlaylists)(this.src).then((playlists) => {
                this.playlists = playlists;
                this.startBitratePoll();
            });
        }
        super.onLoaded();
    }
    onSeeking() {
        if (this.isSeekDisabled) {
            const delta = this.videoElement.currentTime - this.supposedCurrentTime;
            if (Math.abs(delta) > 0.01) {
                this.seekTo(this.supposedCurrentTime);
            }
            return;
        }
        super.onSeeking();
    }
    initDrmSetup() {
        return __awaiter(this, void 0, void 0, function* () {
            this.addVideoEventListener("webkitneedkey", this.onNeedKey);
            this.certificate = yield this.getCertificate();
        });
    }
    getCertificate() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_CERTIFICATE_REQUEST");
            const certificateUrl = (_a = this.drmInfo) === null || _a === void 0 ? void 0 : _a.certificateUrl;
            if (!certificateUrl) {
                return Promise.reject(new js_player_shared_1.PlayerError("Missing Fairplay certificate url", {
                    type: js_player_shared_1.ErrorTypes.DRM,
                }));
            }
            try {
                const response = yield fetch(certificateUrl, {
                    method: "GET",
                    headers: {
                        Pragma: "Cache-Control: no-cache",
                        "Cache-Control": "max-age=0",
                    },
                });
                if (!response.ok) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new js_player_shared_1.PlayerError("The Fairplay certificate could not be fetched", {
                        type: js_player_shared_1.ErrorTypes.DRM,
                    });
                }
                const blob = yield response.arrayBuffer();
                return new Uint8Array(blob);
            }
            catch (error) {
                this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_CERTIFICATE_ERROR");
                throw new js_player_shared_1.PlayerError("The Fairplay certificate could not be fetched", {
                    type: js_player_shared_1.ErrorTypes.DRM,
                });
            }
        });
    }
    getLicense(event) {
        return __awaiter(this, void 0, void 0, function* () {
            this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_LICENSE_REQUEST");
            const licenseUrl = this.drmInfo
                ? new URL(this.drmInfo.licenseServerUrl)
                : undefined;
            if (!licenseUrl) {
                return Promise.reject(new js_player_shared_1.PlayerError("Missing Fairplay license url", {
                    type: js_player_shared_1.ErrorTypes.DRM,
                }));
            }
            try {
                const { target: session, message } = event;
                if (!session) {
                    return Promise.reject(new js_player_shared_1.PlayerError("MediaKeySession not found", {
                        type: js_player_shared_1.ErrorTypes.DRM,
                    }));
                }
                // session is of type WebKitMediaKeySession extended with contentId and keyId properties
                if ("keyId" in session && typeof session.keyId === "string") {
                    licenseUrl.searchParams.set("keyId", session.keyId);
                }
                const response = yield fetch(licenseUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/octet-stream",
                        Authorization: `Bearer ${this.authToken}`,
                    },
                    body: message,
                });
                if (!response.ok) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new js_player_shared_1.PlayerError("The Fairplay license could not be fetched", {
                        type: js_player_shared_1.ErrorTypes.DRM,
                    });
                }
                const blob = yield response.arrayBuffer();
                const license = new Uint8Array(blob);
                // noinspection ES6MissingAwait
                session.update(license);
                this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_LICENSE_RESPONSE");
                return session;
            }
            catch (error) {
                this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_LICENSE_ERROR");
                throw new js_player_shared_1.PlayerError("The Fairplay license could not be fetched", {
                    type: js_player_shared_1.ErrorTypes.DRM,
                });
            }
        });
    }
    concatInitDataIdAndCertificate(initData, id, cert) {
        if (typeof id === "string") {
            id = (0, js_player_shared_1.stringToArray)(id);
        }
        // layout is [initData][4 byte: idLength][idLength byte: id][4 byte:certLength][certLength byte: cert]
        let offset = 0;
        const buffer = new ArrayBuffer(initData.byteLength + 4 + id.byteLength + 4 + cert.byteLength);
        const dataView = new DataView(buffer);
        const initDataArray = new Uint8Array(buffer, offset, initData.byteLength);
        initDataArray.set(initData);
        offset += initData.byteLength;
        dataView.setUint32(offset, id.byteLength, true);
        offset += 4;
        const idArray = new Uint16Array(buffer, offset, id.length);
        idArray.set(id);
        offset += idArray.byteLength;
        dataView.setUint32(offset, cert.byteLength, true);
        offset += 4;
        const certArray = new Uint8Array(buffer, offset, cert.byteLength);
        certArray.set(cert);
        return new Uint8Array(buffer, 0, buffer.byteLength);
    }
    setupSession(keySystem, initData) {
        if (!this.videoElement.webkitKeys) {
            this.videoElement.webkitSetMediaKeys(new WebKitMediaKeys(keySystem));
        }
        return this.videoElement.webkitKeys.createSession("video/mp4", initData);
    }
    onNeedKey(event) {
        var _a;
        if (!((_a = this.drmInfo) === null || _a === void 0 ? void 0 : _a.licenseServerUrl)) {
            return;
        }
        try {
            // initData contains [4 byte: Length][EXT-X-KEY URI]
            const dv = new DataView(event.initData.buffer, event.initData.byteOffset, event.initData.byteLength);
            const initDataLength = dv.getUint32(0, true);
            const xKeyUrl = (0, js_player_shared_1.arrayToString)(dv.buffer.slice(4, 4 + initDataLength));
            const skdUrl = new URL(xKeyUrl);
            if (skdUrl.protocol !== "skd:") {
                throw new Error(`Invalid Fairplay key URL in HLS manifest (skd:// scheme expected): ${xKeyUrl}`);
            }
            const licenseServerUrl = new URL(this.drmInfo.licenseServerUrl);
            this.contentId =
                skdUrl.searchParams.get("contentId") ||
                    licenseServerUrl.searchParams.get("contentId") ||
                    skdUrl.hostname;
            const keyId = skdUrl.searchParams.get("KID") ||
                skdUrl.searchParams.get("keyId") ||
                (0, js_player_shared_1.stringToUUID)(skdUrl.hostname);
            const contentId = this.contentId;
            if (!contentId || !this.certificate) {
                throw "Could not create initData, contentId & certificate missing";
            }
            const initData = this.concatInitDataIdAndCertificate(event.initData, // TODO: the legacy EME being used uses different types for certain things.
            contentId, this.certificate);
            this.keySession = this.setupSession(js_player_shared_1.KeySystem.FAIRPLAY, initData);
            this.keySession.contentId = contentId;
            this.keySession.keyId = keyId;
            if (!this.keySession) {
                throw "Could not create key session";
            }
            this.keySession.addEventListener("webkitkeyadded", this.onKeyAdded, this.keySession);
            this.keySession.addEventListener("webkitkeyerror", this.onKeyError, this.keySession);
            this.keySession.addEventListener("webkitkeymessage", this.onKeyMessage, false);
        }
        catch (err) {
            this.emit(interfaces_1.EngineEvents.ERROR, new js_player_shared_1.PlayerError("[Native] could not initiate drm", {
                type: js_player_shared_1.ErrorTypes.DRM,
                rawError: err,
            }));
        }
    }
    onKeyMessage(event) {
        this.getLicense(event)
            .then((data) => {
            (0, logger_1.debug)("[Native] everything is well", data);
        })
            .catch((err) => {
            this.emit(interfaces_1.EngineEvents.ERROR, err);
        });
    }
    onKeyError() {
        const playerError = (0, NativeErrors_1.convertMediaKeyError)(this.keySession);
        this.emit(interfaces_1.EngineEvents.ERROR, playerError);
    }
    onKeyAdded(event) {
        (0, logger_1.debug)("[Native] Decryption key was added to session.", event);
    }
    getActivePlaylist() {
        var _a;
        if (!((_a = this.playlists) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        const width = this.videoElement.videoWidth;
        const height = this.videoElement.videoHeight;
        return this.playlists.find((playlist) => playlist.width === width && playlist.height === height);
    }
    startBitratePoll() {
        var _a;
        if (!((_a = this.playlists) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        this.bitrateInterval = window.setInterval(() => {
            const playlist = this.getActivePlaylist();
            if (playlist && playlist.bitrate !== this.lastBitrate) {
                this.lastBitrate = playlist.bitrate;
                this.emit(interfaces_1.EngineEvents.BITRATE_CHANGED, {
                    bitrate: playlist.bitrate,
                    width: playlist.width,
                    height: playlist.height,
                });
            }
        }, BITRATE_POLL_INTERVAL);
    }
    stopBitratePoll() {
        clearInterval(this.bitrateInterval);
    }
    onError(error) {
        this.stopBitratePoll();
        if (error) {
            const playerError = (0, NativeErrors_1.convertError)(error);
            if (playerError) {
                this.emit(interfaces_1.EngineEvents.ERROR, playerError);
            }
        }
    }
    setQualityLevel() {
        // not possible
    }
    getSubtitleTrack() {
        const tracks = this.videoElement.textTracks
            ? Array.from(this.videoElement.textTracks)
            : [];
        if (!Array.isArray(tracks) || tracks.length === 0)
            return;
        const activeTrack = tracks.find((track) => {
            return (track.mode === "hidden" &&
                AbstractBaseEngine_1.SUPPORTED_TEXT_TRACK_KINDS.includes(track.kind));
        });
        if (activeTrack) {
            return createTextTrack(activeTrack);
        }
    }
    getSubtitleTracks() {
        const internalTracks = this.videoElement.textTracks
            ? Array.from(this.videoElement.textTracks)
            : [];
        return this.filterVisibleSubtitleTracks(internalTracks
            .map((track) => createTextTrack(track))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind &&
            track.kind === compTrack.kind &&
            AbstractBaseEngine_1.SUPPORTED_TEXT_TRACK_KINDS.includes(track.kind)) === index));
    }
    destroy() {
        this.stopBitratePoll();
        if (this.keySession) {
            this.keySession.close();
            this.keySession = null;
        }
        this.videoElement.removeAttribute("playsinline");
        super.destroy();
    }
}
exports.Native = Native;
//# sourceMappingURL=Native.js.map